import { dialogAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const dialogSlotRecipe = defineSlotRecipe({
  className: 'dialog',
  slots: [
    ...dialogAnatomy.keys(),
    'label',
    'scrollArea',
    'scrollBody',
    'scrollButton',
    'scrollGradient',
    'stepDescription',
    'stepHeader',
    'stepTitle',
    'stepTitleGroup',
  ] as const,
  base: {
    backdrop: {
      background: 'utilities/overlay',
      position: 'fixed',
      inset: 0,
      zIndex: 'calc({zIndex.dialogOverlay} + var(--layer-index, 0))',
      _open: {
        animationStyle: 'fade-in',
        animationDuration: 'moderate',
      },
      _closed: {
        animationStyle: 'fade-out',
        animationDuration: 'moderate',
      },
    },
    positioner: {
      alignItems: { base: 'flex-start', tablet: 'center' },
      display: 'flex',
      height: '100dvh',
      justifyContent: 'center',
      left: 0,
      position: 'fixed',
      top: 0,
      width: '100dvw',
      zIndex: 'calc({zIndex.dialog} + var(--layer-index, 0))',
    },
    content: {
      background: 'background/primary',
      borderRadius: { base: 0, tablet: '8' },
      boxShadow: 'elevation/lg',
      display: 'flex',
      flexDirection: 'column',
      outline: 'none',
      position: 'relative',
      _open: {
        animationStyle: 'scale-fade-in',
        animationDuration: 'moderate',
      },
      _closed: {
        animationStyle: 'scale-fade-out',
        animationDuration: 'faster',
      },
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      gap: '48',
      paddingBlock: '24',
      paddingInline: '32',
      position: 'relative',
    },
    title: {
      color: 'text/primary',
      paddingInlineEnd: '48',
      textStyle: 'heading/h2',
    },
    description: {
      color: 'text/body',
      textStyle: 'body/lg/regular',
    },
    label: {
      color: 'text/secondary',
      textStyle: 'comp/dialog/label',
    },
    body: {
      display: 'flex',
      flex: '1',
      flexDirection: 'column',
      gap: '24',
      paddingInline: '32',
      _last: {
        paddingBlockEnd: '48',
      },
    },
    scrollBody: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      _last: {
        paddingBlockEnd: '48',
      },
    },
    // Hugs the body exactly, so the gradient and the scroll button anchor to the end of the
    // scrollable content. They can't hang off `scrollBody`: that carries the trailing padding,
    // which only applies when no footer follows.
    scrollArea: {
      display: 'flex',
      flex: '1',
      flexDirection: 'column',
      minHeight: 0,
      position: 'relative',
    },
    footer: {
      paddingBlockEnd: '32',
      paddingBlockStart: '24',
      paddingInline: '32',
    },
    closeTrigger: {
      insetEnd: '24',
      position: 'absolute',
      top: '24',
    },
    scrollButton: {
      alignItems: 'center',
      alignSelf: 'center',
      background: 'background/primary',
      borderColor: 'border/minimal',
      borderRadius: '100%',
      borderWidth: '1',
      bottom: '8',
      boxShadow: 'elevation/lg',
      cursor: 'pointer',
      display: 'flex',
      height: '32',
      justifyContent: 'center',
      position: 'absolute',
      width: '32',
    },
    stepDescription: {
      color: 'text/secondary',
      textStyle: 'body/md/regular',
    },
    stepHeader: {
      overflowY: 'auto',
    },
    stepTitle: {
      color: 'text/primary',
      textStyle: 'heading/h3',
    },
    stepTitleGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4',
    },
    scrollGradient: {
      background: 'linear-gradient(0deg, {colors.neutral.100} 0%, transparent 100%)',
      bottom: 0,
      height: '32',
      pointerEvents: 'none',
      position: 'absolute',
      width: '100%',
    },
  },
  variants: {
    scrollBehavior: {
      inside: {
        body: {
          overflowY: 'auto',
          _last: {
            paddingBlockEnd: 0, // override base _last: scrollBody slot handles padding instead
          },
        },
        content: {
          maxHeight: 'calc(100dvh - 96px)',
          overflow: 'hidden',
        },
      },
      outside: {
        positioner: {
          overflow: 'auto',
          paddingBlock: { base: 0, tablet: '10vh' },
        },
        content: {
          marginBlock: { base: 0, tablet: 'auto' },
        },
      },
    },
    size: {
      full: {
        body: {
          minHeight: 0,
          overflowY: 'auto',
        },
        content: {
          borderRadius: '8',
          height: '100%',
          maxHeight: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          width: '100%',
        },
        positioner: {
          padding: '32',
        },
      },
      lg: {
        content: {
          width: { base: '100%', tablet: rem(800) },
        },
      },
      md: {
        content: {
          width: { base: '100%', tablet: rem(640) },
        },
      },
      sm: {
        content: {
          width: { base: '100%', tablet: rem(480) },
        },
      },
    },
    variant: {
      overflowContent: {
        content: {
          maxHeight: 'calc(100dvh - 48px)',
          overflow: 'hidden',
        },
        header: {
          gap: '8',
          paddingBlockEnd: '8',
          paddingBlockStart: '16',
          paddingInline: '16',
        },
        title: {
          paddingInlineEnd: '32',
          textStyle: 'heading/h4',
        },
        body: {
          overflowY: 'auto',
          paddingBlockEnd: '8',
          paddingInline: '16',
          _last: {
            paddingBlockEnd: 0,
          },
        },
        scrollBody: {
          _last: {
            paddingBlockEnd: '16',
          },
        },
        footer: {
          paddingBlockEnd: '16',
          paddingBlockStart: 0,
          paddingInline: '16',
        },
        closeTrigger: {
          insetEnd: '12',
          top: '12',
        },
      },
    },
  },
  defaultVariants: {
    scrollBehavior: 'outside',
    size: 'md',
  },
});

export default dialogSlotRecipe;
